import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Button } from "../ui/button";
import { MarketingLink } from "@/components/OptionalLink";
import { useRouter } from "next/router";
import { useSession } from "@/components/useSession";
import { capitalize, cn, fetcherIgnore404 } from "@/lib/utils";
import { User } from "@/types";
import { JSONSafe } from "@/types";
import useSWR from "swr";
import logger from "@/lib/logger";
import {
  ChevronDown,
  Menu,
  Cog,
  Star,
  MessageSquareText,
  ArrowLeftRight,
  Rocket,
  Flame,
  LayoutGrid,
  BookOpen,
  CircleHelp,
  Users,
  Blocks,
  ExternalLink,
  Monitor,
  Sun,
  Moon,
  Terminal,
  Book,
  FileCode
} from "lucide-react";
import React from "react";
import { useTheme } from "next-themes";
import { ThemeToggle } from "@/components/ThemeToggle";

interface SubRouteProps {
  id: string;
  href: string;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

interface RouteProps {
  id: string;
  href: string;
  label: string;
  active: boolean;
  primary?: boolean;
  secondary?: boolean;
  overflow?: boolean | "only";
  className?: string;
  children?: SubRouteProps[];
}

const isExternal = (href: string) => href.startsWith("http");

const getMenu = (_path: string, signedIn: boolean): RouteProps[] => [
  {
    id: "product",
    href: "/product/about",
    label: "Product",
    active: false,
    overflow: true,
    children: [
      { id: "product-about", href: "/product/about", label: "How It Works", icon: Cog },
      { id: "product-features", href: "/product/features", label: "Features", icon: Star },
      { id: "integrations", href: "/integrations", label: "Integrations", icon: Blocks },
      {
        id: "product-testimonials",
        href: "/product/testimonials",
        label: "Testimonials",
        icon: MessageSquareText
      },
      {
        id: "product-comparison",
        href: "/product/comparison",
        label: "Comparison",
        icon: ArrowLeftRight
      }
    ]
  },
  {
    id: "templates",
    href: "/r",
    label: "Templates",
    active: false,
    overflow: true,
    children: [
      {
        id: "boilerplate",
        href: "/boilerplate",
        label: "Kickstart your project",
        icon: Rocket
      },
      { id: "templates-popular", href: "/popular", label: "Popular templates", icon: Flame },
      { id: "templates", href: "/r", label: "See all templates", icon: LayoutGrid }
    ]
  },
  {
    id: "resources",
    href: "/resources",
    label: "Resources",
    active: false,
    overflow: true,
    children: [
      { id: "blog", href: "/blog", label: "Blog", icon: BookOpen },
      {
        id: "docs",
        href: "https://docs.dollardeploy.com",
        label: "Documentation",
        icon: Book
      },
      {
        id: "faq",
        href: "/faq",
        label: "FAQ",
        icon: CircleHelp
      },
      {
        id: "api-docs",
        href: "/apidocs",
        label: "API Docs",
        icon: FileCode
      },
      {
        id: "cli",
        href: "https://www.npmjs.com/package/@dollardeploy/cli",
        label: "CLI Tool",
        icon: Terminal
      },
      {
        id: "about",
        href: "/about",
        label: "About us",
        icon: Users
      }
    ]
  },
  {
    id: "pricing",
    href: "/pricing",
    label: "Pricing",
    active: false,
    overflow: true
  },
  {
    id: "docs",
    href: "https://docs.dollardeploy.com",
    label: "Documentation",
    active: false,
    overflow: "only"
  },
  ...(signedIn
    ? [
        {
          id: "dashboard",
          href: "/dashboard",
          label: "Dashboard",
          active: false,
          primary: true,
          className: "md:min-w-[100px]"
        }
      ]
    : [
        {
          id: "signUp",
          href: "/auth/signUp?callbackUrl=/dashboard",
          label: "Sign Up",
          active: false,
          primary: true,
          className: "md:min-w-[100px]"
        },
        {
          id: "signIn",
          href: "/auth/signIn?callbackUrl=/dashboard",
          label: "Sign In",
          active: false,
          secondary: true,
          className: "md:min-w-[100px]"
        }
      ])
];

/** Icon only */
export const Icon = ({
  dark = false,
  light = false,
  className = "",
  counter
}: {
  dark?: boolean;
  light?: boolean;
  className?: string;
  counter?: number;
}) => (
  <div className="flex flex-row items-center justify-center relative">
    <img
      src="/logo.svg"
      className={cn("h-5", dark ? "" : light ? "invert-100" : "dark:invert-100", className)}
      alt="DollarDeploy"
    />
    {counter != null && counter > 0 && (
      <span className="absolute bottom-1 right-1 text-[10px] bg-primary border border-secondary text-primary-foreground rounded-full px-1">
        {counter}
      </span>
    )}
  </div>
);

/** Icon + Brand name */
export const Logo = ({ className }: { className?: string }) => (
  <div>
    <img
      src="/logo-main.svg"
      className={cn("h-6 dark:invert-100", className)}
      alt="DollarDeploy"
    />
  </div>
);

export const applyFlags = (user?: Pick<User, "flags">) => {
  if (!user) {
    return;
  }

  if (user.flags.includes("tracking-")) {
    if (!localStorage.getItem("plausible_ignore")) {
      logger.info("Disabling tracking");
      localStorage.setItem("plausible_ignore", "true");
    }
  } else if (localStorage.getItem("plausible_ignore")) {
    logger.info("Enabling tracking");
    localStorage.removeItem("plausible_ignore");
  }
};

const MobileThemeToggle = () => {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: "system", icon: Monitor, label: "System" },
    { value: "light", icon: Sun, label: "Light" },
    { value: "dark", icon: Moon, label: "Dark" }
  ] as const;

  return (
    <div className="flex items-center justify-between px-2">
      <span className="text-sm text-muted-foreground">Theme</span>
      <div className="flex items-center gap-0.5 rounded-full border border-border bg-muted p-0.5">
        {options.map(opt => {
          const active = theme === opt.value;
          return (
            <button
              key={opt.value}
              aria-label={opt.label}
              onClick={() => setTheme(opt.value)}
              className={cn(
                "rounded-full p-1.5 transition-colors",
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <opt.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const navLinkClass =
  "ml-4 text-sm font-normal tracking-tight text-foreground/80 hover:text-foreground flex items-center gap-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm px-1 py-0.5";

const DesktopNavItem = ({ route }: { route: RouteProps }) => {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLAnchorElement | null)[]>([]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!route.children) {
      return;
    }

    if (e.key === "Escape") {
      setOpen(false);
      menuRef.current?.querySelector("button")?.focus();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      }
      itemRefs.current[0]?.focus();
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open) {
        const last = itemRefs.current[itemRefs.current.length - 1];
        last?.focus();
      }
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = itemRefs.current[index + 1] ?? itemRefs.current[0];
      next?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev =
        itemRefs.current[index - 1] ?? itemRefs.current[itemRefs.current.length - 1];
      prev?.focus();
    } else if (e.key === "Escape") {
      setOpen(false);
      menuRef.current?.querySelector("button")?.focus();
    }
  };

  if (!route.children || route.children.length === 0) {
    return (
      <MarketingLink
        href={route.href}
        className={cn(navLinkClass, `plausible-event-name=${capitalize(route.id)}`)}
      >
        {route.label}
      </MarketingLink>
    );
  }

  return (
    <div
      ref={menuRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={handleKeyDown}
    >
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          navLinkClass,
          open && "text-foreground",
          `plausible-event-name=${capitalize(route.id)}`
        )}
      >
        {route.label}
        <ChevronDown
          strokeWidth={1.5}
          className={cn("h-3 w-3 transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      <div
        role="menu"
        className={cn(
          "absolute top-full left-0 z-50 min-w-[190px] pt-1",
          open ? "pointer-events-auto" : "pointer-events-none"
        )}
      >
        <div
          className={cn(
            "rounded-lg border border-border bg-popover shadow-md p-1",
            "transition-all duration-150 ease-out",
            open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
          )}
        >
          {route.children.map((child, index) => (
            <a
              key={child.id}
              ref={(el: HTMLAnchorElement | null) => {
                itemRefs.current[index] = el;
              }}
              href={child.href}
              {...(isExternal(child.href)
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              role="menuitem"
              tabIndex={open ? 0 : -1}
              onClick={() => setOpen(false)}
              onKeyDown={(e: React.KeyboardEvent) => handleItemKeyDown(e, index)}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 w-full",
                "text-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                "focus-visible:outline-none focus-visible:bg-accent focus-visible:text-accent-foreground",
                `plausible-event-name=${capitalize(child.id)}`
              )}
            >
              {child.icon && (
                <child.icon
                  className="h-4 w-4 text-muted-foreground shrink-0"
                  strokeWidth={1.5}
                />
              )}
              <span className="font-medium flex-1">{child.label}</span>
              {isExternal(child.href) && (
                <ExternalLink
                  className="h-3 w-3 text-muted-foreground shrink-0"
                  strokeWidth={1.5}
                />
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileNavItem = ({ route, onClose }: { route: RouteProps; onClose: () => void }) => {
  const [open, setOpen] = React.useState(false);

  if (!route.children || route.children.length === 0) {
    return (
      <Button
        asChild
        onClick={onClose}
        variant={"ghost"}
        animations="none"
        className={cn("justify-start", `plausible-event-name=${capitalize(route.id)}`)}
      >
        <MarketingLink href={route.href}>{route.label}</MarketingLink>
      </Button>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild className="ml-1">
        <Button
          variant="ghost"
          animations="none"
          className={cn(
            "w-full justify-between",
            `plausible-event-name=${capitalize(route.id)}`
          )}
        >
          {route.label}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-1 pl-3 pt-1 border-l border-border ml-3">
        {route.children.map(child => (
          <MarketingLink
            key={child.id}
            href={child.href}
            onClick={onClose}
            {...(isExternal(child.href)
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm",
              "hover:bg-accent hover:text-accent-foreground transition-colors",
              `plausible-event-name=${capitalize(child.id)}`
            )}
          >
            {child.icon && (
              <child.icon
                className="h-4 w-4 text-muted-foreground shrink-0"
                strokeWidth={1.5}
              />
            )}
            <span className="font-medium flex-1">{child.label}</span>
            {isExternal(child.href) && (
              <ExternalLink
                className="h-3 w-3 text-muted-foreground shrink-0"
                strokeWidth={1.5}
              />
            )}
          </MarketingLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const Navbar = () => {
  const router = useRouter();
  const { data: session } = useSession();
  const { data: user } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    applyFlags(user);
  }, [user]);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(globalThis.scrollY > 8);
    };
    globalThis.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => globalThis.removeEventListener("scroll", handleScroll);
  }, []);

  const currentMenu = getMenu(router.pathname, !!session?.user);

  const desktopNavItems = currentMenu.filter(
    route => route.overflow !== "only" && !route.primary && !route.secondary
  );
  const ctaItems = currentMenu.filter(route => !route.overflow);
  const mobileNavItems = currentMenu.filter(route => !route.primary && !route.secondary);
  const mobileAuthItems = currentMenu.filter(route => route.primary || route.secondary);

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:text-sm focus:font-medium"
      >
        Skip to content
      </a>
      <header
        className={cn(
          "fixed top-0 left-0 w-full z-50 transition-[background-color,border-color,box-shadow] duration-200",
          scrolled
            ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-sm"
            : "bg-transparent border-b border-transparent"
        )}
      >
        <nav
          aria-label="Main navigation"
          className="container max-w-[60rem] flex items-center justify-between py-2.5"
        >
          <div className="flex items-center">
            <MarketingLink
              href="/#stay"
              className="plausible-event-name=Home focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            >
              <Logo className="h-6" />
            </MarketingLink>

            <div className="hidden md:flex items-center">
              {desktopNavItems.map(route => (
                <DesktopNavItem key={route.id} route={route} />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {ctaItems.map(route => (
              <Button
                asChild
                key={route.id}
                size="sm"
                variant={route.primary ? "default" : "ghost"}
                className={cn(
                  "text-sm font-medium tracking-tight",
                  route.primary && "shadow-sm",
                  route.secondary && "hidden md:inline-flex",
                  route.className,
                  `plausible-event-name=${capitalize(route.id)}`
                )}
              >
                <MarketingLink href={route.href}>{route.label}</MarketingLink>
              </Button>
            ))}

            <div className="hidden md:block">
              <ThemeToggle />
            </div>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open menu">
                  <Menu className="h-5 w-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-3/4 max-w-sm pl-2 flex flex-col">
                <SheetHeader>
                  <SheetTitle>
                    <Logo className="h-5" />
                  </SheetTitle>
                </SheetHeader>
                <nav
                  aria-label="Mobile navigation"
                  className="flex flex-col gap-1 mt-4 flex-1 px-2"
                >
                  {mobileNavItems.map(route => (
                    <MobileNavItem
                      key={route.id}
                      route={route}
                      onClose={() => setIsOpen(false)}
                    />
                  ))}

                  <div className="border-t border-border mt-3 pt-3 flex flex-col gap-1">
                    {mobileAuthItems.map(route => (
                      <MobileNavItem
                        key={route.id}
                        route={route}
                        onClose={() => setIsOpen(false)}
                      />
                    ))}
                  </div>

                  <div className="border-t border-border mt-3 pt-3">
                    <MobileThemeToggle />
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>
    </>
  );
};
